import React, { useEffect, useState } from 'react';
import { Select, Tag, Button, message } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';

const { Option } = Select;

// Filters, search and paging live in the URL so a filtered view can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  category: { type: 'string' },
  status: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
};

const WhatsAppTemplatesTable = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Filters
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  // Any filter change goes back to the first page.
  const urlSetter = (name) => (value) => setFilters({ [name]: value, page: 1 });
  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);
  const categoryFilter = filters.category || undefined;
  const setCategoryFilter = urlSetter('category');
  const statusFilter = filters.status || undefined;
  const setStatusFilter = urlSetter('status');

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [dataSource, searchTerm, categoryFilter, statusFilter]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // Paged client-side, so load up to the largest page size on offer.
      const response = await axios.get('/reminders/whatsapp-templates/', { params: { page_size: MAX_PAGE_SIZE } });
      if (response.status === 200) {
        setDataSource(response.data.results || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...dataSource];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.body_text.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter) {
      filtered = filtered.filter(item => item.template_category === categoryFilter);
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(item => item.approval_status === statusFilter);
    }

    setFilteredData(filtered);
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      APPROVED: { color: 'success', text: 'Approved' },
      PENDING: { color: 'warning', text: 'Pending' },
      REJECTED: { color: 'error', text: 'Rejected' },
    };

    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Template ID',
      dataIndex: 'whatsapp_template_id',
      key: 'whatsapp_template_id',
      width: 180,
      render: (text) => <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{text}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'template_type_display',
      key: 'template_type',
      width: 100,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Category',
      dataIndex: 'template_category_display',
      key: 'template_category',
      width: 120,
      render: (text) => <Tag color="purple">{text}</Tag>,
    },
    {
      title: 'Language',
      dataIndex: 'template_language',
      key: 'template_language',
      width: 80,
    },
    {
      title: 'Body Text',
      dataIndex: 'body_text',
      key: 'body_text',
      width: 300,
      ellipsis: true,
      render: (text) => (
        <div style={{ fontSize: '12px', color: '#666' }}>
          {text?.substring(0, 100)}{text?.length > 100 ? '...' : ''}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Usage Count',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 100,
      sorter: (a, b) => a.usage_count - b.usage_count,
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 140,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : 'Never',
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active) => active ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>WhatsApp Templates</h2>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={() => {
              messageApi.info('Sync functionality to be implemented with Meta Business API');
            }}
          >
            Sync from Meta
          </Button>
        </div>

        <FilterBar
          searchValue={searchTermLive}
          onSearchChange={(value) => { setSearchTermLive(value); setSearchTerm(value); }}
          onSearch={setSearchTerm}
          searchPlaceholder="Search templates"
          activeCount={(categoryFilter ? 1 : 0) + (statusFilter ? 1 : 0)}
          onClear={() => setFilters({ category: null, status: null, page: 1 })}
          title="Filter Templates"
          onRefresh={() => fetchTemplates()}
          refreshing={loading}
        >
          <FilterField label="Category">
            <Select
              placeholder="Category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              allowClear
            >
              <Option value="TRANSACTIONAL">Transactional</Option>
              <Option value="MARKETING">Marketing</Option>
              <Option value="OTP">OTP</Option>
            </Select>
          </FilterField>

          <FilterField label="Status">
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
            >
              <Option value="APPROVED">Approved</Option>
              <Option value="PENDING">Pending</Option>
              <Option value="REJECTED">Rejected</Option>
            </Select>
          </FilterField>
        </FilterBar>

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1400 }}
          pagination={{
            current: filters.page,
            pageSize: Math.min(filters.pageSize, MAX_PAGE_SIZE),
            onChange: (page, size) => setFilters({ page: size !== filters.pageSize ? 1 : page, pageSize: size }),
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} templates`,
          }}
        />
      </div>
    </>
  );
};

export default WhatsAppTemplatesTable;
